import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, input, model, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete/public_api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { DocumentiService } from 'src/app/core/documenti.service';
import { autocompleteFilter } from '../helpers/autocompleteFilter.helper';
import { StatiPredefiniti } from '../models/stati-predefiniti';
import { Stato } from '../models/stato.model';
import { Utente } from '../models/utente.model';

@Component({
  selector: 'app-modifica-stato-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <p-dialog #dialogstato class="status-dialog" header="Modifica Stato" [(visible)]="visible" [modal]="true"
    [contentStyle]="{ width: '300px' }" (onHide)="resetFormAndHide()">
    <form [formGroup]="stateChangeForm" (ngSubmit)="modifyState()">
      <div>
        <label for="utente">Utente</label>
        <div class="value">
          <p-autoComplete formControlName="utente" [suggestions]="utentiFiltrati"
            (completeMethod)="autocompleteUtenti($event)" [dropdown]="true"
            [size]="20" [forceSelection]="false" [autoHighlight]="true" [inputStyle]="{'width': '200px'}" appendTo="body">
          </p-autoComplete>
        </div>
      </div>
      <div>
        <label for="nuovoStato">Nuovo stato</label>
        <div class="value">
          <p-autoComplete formControlName="nuovoStato" [suggestions]="statiVisibiliFiltrati"
            (completeMethod)="autocompleteStati($event)" [dropdown]="true"
            [size]="20" [forceSelection]="true" [autoHighlight]="true" field="nome" [inputStyle]="{'width': '200px'}"
            appendTo="body">
          </p-autoComplete>
        </div>
      </div>
      <div>
        <label for="dataInizio">Inizio Stato dal</label>
        <div class="value">
          <p-calendar [showIcon]="true" dateFormat="dd/mm/yy" [selectOtherMonths]="true" appendTo="body"
            formControlName="dataInizio" [inputStyle]="{'width': '200px'}">
          </p-calendar>
        </div>
      </div>
      <div>
        <label for="dataScadenza">Data Scadenza</label>
        <div class="value">
          <p-calendar [showIcon]="true" dateFormat="dd/mm/yy" [selectOtherMonths]="true" appendTo="body"
            formControlName="dataScadenza" [inputStyle]="{'width': '200px'}">
          </p-calendar>
        </div>
      </div>
      <div>
        <label for="note">Note</label>
        <div class="value"><input pInputText type="text" placeholder="" formControlName="note" id="note"></div>
      </div>
    </form>
    <p-footer>
      <button pButton type="button" icon="pi pi-times" label="Annulla" title="Annulla" class="p-button-secondary"
        (click)="resetFormAndHide()"></button>
      <button pButton type="button" icon="pi pi-check" label="Modifica" title="Modifica"
        [disabled]="!stateChangeForm.valid" (click)="modifyState()"></button>
    </p-footer>
  </p-dialog>
  `
})

export class ModificaStatoDialogComponent implements OnInit, OnDestroy {
  visible = model.required<boolean>();
  documentId = input<number | undefined>();

  hide = output();
  done = output<number>();
  fail = output<HttpErrorResponse>();

  public stateChangeForm!: FormGroup<NewStateForm>;
  public autocompleteFilter = autocompleteFilter;
  public statiVisibili: Stato[] = [];
  public statiVisibiliFiltrati: Stato[] = [];
  public utenti: Utente[] = [];
  public utentiFiltrati: Utente[] = [];

  private subscriber$ = new Subject<void>();

  private fb = inject(FormBuilder);
  private anagraficheService = inject(AnagraficheService);
  private documentiService = inject(DocumentiService)

  ngOnInit() {
    this.stateChangeForm = this.fb.group<NewStateForm>({
      // eslint-disable-next-line @typescript-eslint/unbound-method
      utente: this.fb.nonNullable.control<string>('', Validators.compose([Validators.required, Validators.minLength(1)])),
      // eslint-disable-next-line @typescript-eslint/unbound-method
      nuovoStato: this.fb.control<Stato | null>(null, Validators.required),
      // eslint-disable-next-line @typescript-eslint/unbound-method
      dataInizio: this.fb.nonNullable.control<Date>(new Date(), Validators.required),
      // eslint-disable-next-line @typescript-eslint/unbound-method
      dataScadenza: this.fb.control<Date | null>(null, Validators.required),
      note: this.fb.nonNullable.control<string>('')
    });

    this.stateChangeForm.controls.dataScadenza.disable();

    this.anagraficheService.utenti.getAllArray(u => this.utenti = u);
    this.anagraficheService.stati.getAllVisibili().pipe(
      takeUntil(this.subscriber$),
    ).subscribe(s => this.statiVisibiliFiltrati = this.statiVisibili = s);

    this.stateChangeForm.controls.nuovoStato.valueChanges
      .pipe(
        takeUntil(this.subscriber$)
      )
      .subscribe((selectedState: Stato | null) => {
        if (selectedState) {
          // abilita nome utente e scadenza solo per le evidenze
          const element = this.stateChangeForm.controls.dataScadenza;
          if (selectedState.statoId !== StatiPredefiniti.Evidenza as number) {
            element.reset();
            element.disable({ emitEvent: false });
          } else {
            element.enable({ emitEvent: false });
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriber$.next();
    this.subscriber$.complete();
  }

  autocompleteStati(e: AutoCompleteCompleteEvent) {
    this.statiVisibiliFiltrati = autocompleteFilter(e, this.statiVisibili, 'nome')
  }

  autocompleteUtenti(e: AutoCompleteCompleteEvent) {
    this.utentiFiltrati = autocompleteFilter(e, this.utenti, 'nomeUtente', true)
  }

  hideDialog(): void {
    this.visible.set(false);
    this.hide.emit();
  }

  modifyState(): void {
    const documentId = this.documentId();
    if (!this.stateChangeForm.valid || !documentId) {
      return;
    }

    // aggiunti per gestione reactive form
    const utente = this.stateChangeForm.controls.utente.value;
    const nuovoStato = this.stateChangeForm.controls.nuovoStato.value;
    const dataInvio = this.stateChangeForm.controls.dataInizio.value;
    const dataScadenza = this.stateChangeForm.controls.dataScadenza.value;
    const note = this.stateChangeForm.controls.note.value;

    if (nuovoStato)
      this.documentiService.changeState(documentId, nuovoStato.statoId, utente, note, dataInvio, dataScadenza)
        .subscribe(res => {
          this.resetFormAndHide();
          this.done.emit(res);
        });
  }

  resetFormAndHide(): void {
    this.stateChangeForm.reset();
    this.hideDialog();
  }
}

interface NewStateForm {
  utente: FormControl<string>;
  nuovoStato: FormControl<Stato | null>;
  dataInizio: FormControl<Date>;
  dataScadenza: FormControl<Date | null>;
  note: FormControl<string>;
}
